import React from 'react';
import { Shield, Lock, Eye, MessageCircle, Users, AlertTriangle, FileText, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import SiteNav from '../components/SiteNav';
import PageMeta from '../components/PageMeta';
import TrustedPartners from '../components/TrustedPartners';

/**
 * Teen Safety & Privacy — parent-facing summary of how HiBFF protects teens.
 * Linked from the homepage footer (S-049). Sister page to /privacy.
 */
const TeenSafety = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="teen-safety-page">
      <PageMeta
        title="Teen Safety at HiBFF — How We Keep Chat Safe"
        description="Every HiBFF conversation runs through a safety layer built for teens: no clinical advice, instant human handover in a crisis, and parents approve every account."
        path="/teen-safety"
        slug="teen-safety"
      />
      <SiteNav />
      <div className="bg-purple-50 border-b border-purple-100 px-4 py-2 text-center text-sm">
        <Link to="/trust-safety" className="text-purple-600 hover:text-purple-800 font-semibold" data-testid="teen-safety-trust-link">Live safety stats →</Link>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ backgroundColor: '#FFF5EA' }}>
            <Shield size={32} style={{ color: '#F5831F' }} />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-3">Teen Safety & Privacy</h1>
          <p className="text-lg text-gray-600">How HiBFF protects your teen — and why parents trust us with the conversation.</p>
        </div>

        <p className="text-base text-gray-700 mb-8">
          HiBFF is built for teenagers aged 13+. Every design decision starts from one question:
          <em>&quot;would I want my own teenager using this?&quot;</em> If the answer isn&apos;t an immediate yes, we
          don&apos;t ship it. This page is the plain-English version of how we deliver on that.
        </p>

        <Section icon={Lock} title="Conversations are private — even from parents">
          <p>
            Your teen&apos;s chats with their AvA stay between them and the AI. Parents never see the words.
            Schools never see the words. We don&apos;t read them, store them in plain text, or share them
            with third parties for marketing. After 90 days they auto-delete from our systems entirely.
          </p>
          <p>
            What parents <strong>do</strong> see: a monthly summary showing how often their teen
            practiced and which milestones they hit. Frequency, not content. Your teen can disable
            even this from their dashboard at any time — they own the visibility decision.
          </p>
        </Section>

        <Section icon={Eye} title="Real-time safety guardrails">
          <p>
            Every message sent and received is screened across 9 safety domains: self-harm,
            violence, sexual content, predatory grooming, illegal activity, hate, eating-disorder
            triggers, substance abuse and crisis indicators. Anything matching is blocked
            <strong> before</strong> it reaches the teen, with the trained-human Crisis Pathway
            triggered automatically when warranted.
          </p>
          <p>
            Our AI Practice Partners (AvAs) are tuned with stricter guardrails than any general-purpose
            chatbot — no romantic content, no graphic descriptions, no &quot;do anything&quot; prompts. They
            never escalate, always redirect, and always remind your teen they can talk to a human.
          </p>
        </Section>

        <Section icon={MessageCircle} title="Crisis pathway — one tap to a real human">
          <p>
            Every screen has a Crisis button. One tap connects your teen to a trained mental-health
            professional within minutes — anonymously if they wish. We&apos;re not a clinical service;
            we triage, support, and connect them onward to local resources where it matters.
          </p>
          <p>
            Crisis sessions are <strong>not</strong> visible to parents by default. We respect your
            teen&apos;s autonomy — but when there is imminent risk to life, we follow the duty-of-care
            protocols expected of any safeguarding-led service.
          </p>
        </Section>

        <Section icon={Users} title="What schools and corporates see">
          <p>
            For school deployments: school admins see <strong>aggregate, anonymised</strong> wellbeing
            indicators across the cohort — never individual student conversations. They see
            &quot;12 students flagged a low-mood day this week&quot; but never which 12 or what they said.
          </p>
          <p>
            For corporate (parent-as-employee) deployments: HR sees adoption and engagement metrics
            only. Never individual employee or family content. Aggregated, never personalised.
          </p>
          <p>
            We carry SOC 2 Type II readiness and full GDPR + UK Age Appropriate Design Code
            compliance — both audited annually.
          </p>
        </Section>

        <Section icon={Heart} title="What your teen earns is theirs">
          <p>
            Hearts, badges, achievements, NFTs, virtual badges — they are owned by your teen,
            not us. They can keep them, gift them to a friend, redeem physical badges to be mailed
            to your home, or save them with HiBFF for later. This isn&apos;t loyalty data we monetise —
            it&apos;s the digital proof of the work they did.
          </p>
        </Section>

        <Section icon={AlertTriangle} title="What we don't do">
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>We don&apos;t sell or share teen data with advertisers.</li>
            <li>We don&apos;t allow ads of any kind in the teen experience.</li>
            <li>We don&apos;t read conversations (except automated safety screening).</li>
            <li>We don&apos;t keep messages longer than 90 days.</li>
            <li>We don&apos;t expose teen identity outside their own household.</li>
            <li>We don&apos;t allow strangers to contact your teen — peer features are opt-in, school-mediated, and moderated.</li>
          </ul>
        </Section>

        <Section icon={FileText} title="Your rights as a parent or guardian">
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Request a full export of your teen&apos;s account data at any time.</li>
            <li>Delete your teen&apos;s account and all associated data with one click.</li>
            <li>Pause your teen&apos;s subscription for 90 days (no billing) or indefinitely.</li>
            <li>Receive monthly summary reports — frequency only, never content.</li>
            <li>30-day full refund window on any paid plan.</li>
            <li>Direct line to safeguarding via <a href="mailto:safety@hibff.com" className="text-purple-600 underline">safety@hibff.com</a>.</li>
          </ul>
        </Section>

        <div className="mt-16 p-6 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(245,131,31,0.08), rgba(123,79,216,0.08))', border: '1px solid #F5C99F' }}>
          <h2 className="text-lg font-bold text-gray-900 mb-2">More detail</h2>
          <p className="text-sm text-gray-700">
            For the full, lawyer-reviewed Privacy Policy and Terms of Service — including data
            controllers, retention windows, lawful bases, and EU/UK transfer mechanisms — see:
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <Link to="/privacy" className="text-sm font-bold text-purple-600 underline">Privacy Policy →</Link>
            <Link to="/terms" className="text-sm font-bold text-purple-600 underline">Terms of Service →</Link>
            <a href="mailto:support@hibff.com" className="text-sm font-bold text-purple-600 underline">support@hibff.com</a>
          </div>
        </div>

        <div className="mt-10 p-6 bg-white rounded-2xl border border-gray-200" data-testid="crisis-partners-block">
          <TrustedPartners variant="inline" />
        </div>

        <p className="text-xs text-gray-500 text-center mt-12">
          HiBFF Ltd · Last updated April 2026
        </p>
      </main>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="mb-10" data-testid={`section-${title.split(' ')[0].toLowerCase()}`}>
    <h2 className="text-xl font-black text-gray-900 mb-3 flex items-center gap-2">
      <Icon size={20} className="text-purple-600 flex-shrink-0" />
      {title}
    </h2>
    <div className="space-y-3 text-base text-gray-700 leading-relaxed">
      {children}
    </div>
  </section>
);

export default TeenSafety;
